import { DownloadIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getTrackingReports } from '@/features/projects/kpi-tracking-entry/api/get-tracking-reports';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown/dropdown';
import { toast } from '@/shared/hooks/use-toast';
import { formatDate } from '@/shared/utils/format';

interface TimeRangeOption {
  labelKey: string;
  monthsBack?: number;
}

const timeRangeOptions: TimeRangeOption[] = [
  { labelKey: '1_month', monthsBack: 1 },
  { labelKey: '3_months', monthsBack: 3 },
  { labelKey: '6_months', monthsBack: 6 },
  { labelKey: '12_months', monthsBack: 12 },
  { labelKey: 'all_time', monthsBack: undefined },
];

export const ExportTrackingReportsButton = () => {
  const { t } = useTranslation('project', {
    keyPrefix: 'TrackingReports.Export',
  });
  const { projectId } = useParams();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (monthsBack?: number) => {
    if (!projectId) return;

    setIsExporting(true);
    try {
      const reports = await getTrackingReports(Number(projectId), monthsBack);

      if (reports.length === 0) {
        toast({ title: t('no_data') });
        return;
      }

      const csvContent = convertToCSV(reports, t as any);
      downloadCSV(csvContent, `tracking-reports-${projectId}.csv`);

      toast({ title: t('success') });
    } catch {
      toast({ title: t('error'), variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" btnType="secondary" disabled={isExporting}>
          <DownloadIcon />
          {t('button_label')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent title={t('title')} align="end">
        {timeRangeOptions.map((option) => (
          <DropdownMenuItem
            key={option.labelKey}
            onClick={() => handleExport(option.monthsBack)}
          >
            {t(option.labelKey as '1_month')}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function getReportDateLabel(
  report: Awaited<ReturnType<typeof getTrackingReports>>[number],
  editedLabel: string,
): string {
  const editedEntries = report.tracking_entries.filter(
    (entry) =>
      entry.updated_at &&
      new Date(entry.updated_at).getTime() !==
        new Date(entry.created_at).getTime(),
  );
  if (editedEntries.length > 0) {
    const latestEditDate = editedEntries.reduce((latest, entry) => {
      const d = new Date(entry.updated_at!);
      return d > latest ? d : latest;
    }, new Date(editedEntries[0].updated_at!));
    return `${formatDate({ date: latestEditDate, formatType: 'digits' })} ${editedLabel}`;
  }
  return formatDate({ date: report.created_at, formatType: 'digits' });
}

function convertToCSV(
  reports: Awaited<ReturnType<typeof getTrackingReports>>,
  t: (key: string) => string,
): string {
  const headers = [
    t('csv_headers.date'),
    t('csv_headers.project'),
    t('csv_headers.creator'),
    t('csv_headers.description'),
    t('csv_headers.kpi_name'),
    t('csv_headers.kpi_value'),
  ];

  const editedLabel = t('csv_headers.edited');
  const rows: string[][] = [];

  for (const report of reports) {
    const dateLabel = getReportDateLabel(report, editedLabel);

    if (report.tracking_entries.length === 0) {
      rows.push([
        dateLabel,
        report.project.name,
        report.creator_entity_name ?? '',
        report.description ?? '',
        '',
        '',
      ]);
    } else {
      for (const entry of report.tracking_entries) {
        rows.push([
          dateLabel,
          report.project.name,
          report.creator_entity_name ?? '',
          report.description ?? '',
          entry.kpi.name,
          entry.value?.toString() ?? '',
        ]);
      }
    }
  }

  const escapeCSVField = (field: string) => {
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  };

  const csvRows = [
    headers.map(escapeCSVField).join(','),
    ...rows.map((row) => row.map(escapeCSVField).join(',')),
  ];

  return csvRows.join('\n');
}

function downloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
